// src/pages/projects/ProjectCreate.jsx

import { useEffect, useState } from "react";
import { collection, getDocs, query, where, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useNavigate, useLocation } from "react-router-dom";
import { createProject, TEMPLATE_MAP } from "./projectService";
import { convertQuotationToProject } from "../../services/quotationService";
import { notifyEnquiryConverted } from "../../services/notificationService";
import {
  QuickCreateButton,
  QuickCreateModal,
  QuickCreateClient,
  QuickCreateSite,
} from "../../components/quickcreate";

export default function ProjectCreate() {
  const navigate   = useNavigate();
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";

  const [clients, setClients] = useState([]);
  const [sites,   setSites]   = useState([]);
  const [saving,       setSaving]       = useState(false);
  const [qcOpen,       setQcOpen]       = useState(null);
  const [templateKeys, setTemplateKeys] = useState(["vrf"]); // multi-select, was single templateKey

  const [form, setForm] = useState({
    project_name:  "",
    client_id:     "",
    client_name:   "",
    site_id:       "",
    site_name:     "",
    project_type:  "installation",
    total_value:   "",
    start_date:    "",
    end_date:      "",
    description:   "",
    load_template: true,
    template_keys: ["vrf"],
  });

  // Contacts — Virender, Amit Govil etc
  const [contacts, setContacts] = useState([
    { name: "", role: "", phone: "" }
  ]);

  const location = useLocation();

  useEffect(() => {
    fetchClients();

    // Pre-fill from Quotation
    if (location.state?.from_quotation) {
      const s = location.state;
      setForm(f => ({
        ...f,
        client_id:    s.client_id    || "",
        client_name:  s.client_name  || "",
        site_id:      s.site_id      || "",
        site_name:    s.site_name    || "",
        project_type: s.project_type || "installation",
        total_value:  s.total_value  || "",
        description:  s.description  || "",
        project_name: s.description  || "",
      }));
      if (s.client_id) fetchSites(s.client_id);
    }

    // ✅ Pre-fill from Enquiry — client auto-filled, SM selects site + HVAC type
    if (location.state?.from_enquiry) {
      const s = location.state;
      setForm(f => ({
        ...f,
        client_id:    s.client_id    || "",
        client_name:  s.client_name  || "",
        project_name: s.project_name || "",
        description:  s.description  || "",
        total_value:  s.total_value  || "",
        project_type: s.project_type || "vrf_installation", // ✅ HVAC default
      }));
      if (s.client_id) fetchSites(s.client_id);
    }
  }, []); // eslint-disable-line

  const fetchClients = async () => {
    const snap = await getDocs(query(
      collection(db, "clients"),
      where("company_id", "==", company_id)
    ));
    setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const fetchSites = async (clientId) => {
    if (!clientId) return;
    const snap = await getDocs(query(
      collection(db, "sites"),
      where("company_id", "==", company_id),
      where("client_id",  "==", clientId)
    ));
    setSites(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const addContact    = () =>
    setContacts([...contacts, { name: "", role: "", phone: "" }]);

  const removeContact = (i) =>
    setContacts(contacts.filter((_, idx) => idx !== i));

  const updateContact = (i, field, val) => {
    const updated = [...contacts];
    updated[i][field] = val;
    setContacts(updated);
  };

  const handleSave = async () => {
    if (!form.project_name.trim()) { alert("Project name required"); return; }
    if (!form.client_id)           { alert("Select a client"); return; }
    if (!form.site_id) {
      alert(
        sites.length === 0
          ? "This client has no sites yet. Please add a site first (use the + button next to Site)."
          : "Please select a site."
      );
      return;
    }
    if (form.load_template && templateKeys.length === 0) {
      alert("Select at least one trade template, or turn off 'Load checklist'.");
      return;
    }

    try {
      setSaving(true);
      const validContacts = contacts.filter(c => c.name.trim());
      const projectId = await createProject(
        { ...form, contacts: validContacts, template_keys: templateKeys },
        user
      );

      // 🆕 Fix 4 — project_id save back on quotation
      if (location.state?.from_quotation && location.state?.quotation_id) {
        try {
          await convertQuotationToProject(location.state.quotation_id, projectId);
        } catch (err) {
          console.error("convertQuotationToProject failed:", err);
          // non-blocking — project already created
        }
      }

      // ✅ Enquiry → Project convert — markConverted + notify
      if (location.state?.from_enquiry && location.state?.enquiry_id) {
        try {
          await updateDoc(doc(db, "enquiries", location.state.enquiry_id), {
            converted:    true,
            converted_to: "project",
            converted_id: projectId,
            converted_at: serverTimestamp(),
            converted_by: user.uid || user.id || "",
            status:       "Won",
            updated_at:   serverTimestamp(),
          });
          await notifyEnquiryConverted({
            enquiry_id:   location.state.enquiry_id,
            enquiry_no:   location.state.enquiry_no || "",
            client_name:  location.state.client_name || form.client_name,
            converted_to: "Project",
            company_id:   company_id,
          });
        } catch (err) {
          console.error("enquiry markConverted failed:", err);
          // non-blocking — project already created
        }
      }

      alert("Project created ✅");
      navigate(`/projects/${projectId}`);

    } catch (err) {
      console.error("createProject error:", err);
      alert("Failed to create project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-3xl mx-auto">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">New Project</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {location.state?.from_quotation
              ? `From Quotation: ${location.state.quotation_no}`
              : "Fill details to create project"}
          </p>
        </div>
        <button onClick={() => navigate("/projects")}
          className="text-gray-500 hover:text-gray-800 text-sm">← Back</button>
      </div>

      {/* BASIC INFO */}
      <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
        <h2 className="font-semibold text-gray-700">Basic Information</h2>

        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1">
            Project Name *
          </label>
          <input
            value={form.project_name}
            onChange={e => setForm({ ...form, project_name: e.target.value })}
            placeholder="e.g. Bank of Baroda — HVAC Installation"
            className="border p-2 rounded-xl w-full text-sm outline-none focus:ring-2 focus:ring-green-300"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">
              Client *
            </label>
            <div className="flex gap-2 items-center">
              <select
                value={form.client_id}
                onChange={e => {
                  const c = clients.find(c => c.id === e.target.value);
                  if (!c) return;
                  setForm({ ...form,
                    client_id: c.id, client_name: c.client_name,
                    site_id: "", site_name: ""
                  });
                  fetchSites(c.id);
                }}
                className="border p-2 rounded-xl flex-1 text-sm outline-none bg-white">
                <option value="">Select Client</option>
                {clients.map(c =>
                  <option key={c.id} value={c.id}>{c.client_name}</option>
                )}
              </select>
              <QuickCreateButton onClick={() => setQcOpen("client")} label="Add client" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Site</label>
            <div className="flex gap-2 items-center">
              <select
                value={form.site_id}
                onChange={e => {
                  const s = sites.find(s => s.id === e.target.value);
                  if (!s) return;
                  setForm({ ...form, site_id: s.id, site_name: s.site_name || s.site_title });
                }}
                disabled={!form.client_id}
                className="border p-2 rounded-xl flex-1 text-sm outline-none bg-white disabled:opacity-50">
                <option value="">Select Site</option>
                {sites.map(s =>
                  <option key={s.id} value={s.id}>{s.site_name || s.site_title}</option>
                )}
              </select>
              <QuickCreateButton
                onClick={() => setQcOpen("site")}
                label="Add site"
                disabled={!form.client_id}
              />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">
              Project Type
            </label>
            <select
              value={form.project_type}
              onChange={e => setForm({ ...form, project_type: e.target.value })}
              className="border p-2 rounded-xl w-full text-sm outline-none bg-white">
              <optgroup label="Installation Type">
                <option value="vrf_installation">VRF / VRV Installation</option>
                <option value="chiller_installation">Chiller Installation</option>
                <option value="split_installation">Split AC Installation</option>
                <option value="ducting_installation">Ducting Installation</option>
                <option value="precision_installation">Precision AC Installation</option>
                <option value="ventilation_installation">Ventilation / AHU</option>
              </optgroup>
              <optgroup label="Other">
                <option value="amc">AMC</option>
                <option value="maintenance">Maintenance</option>
                <option value="supply">Supply Only</option>
                <option value="installation">General Installation</option>
                <option value="mixed">Mixed / Multi-Trade</option>
              </optgroup>
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">
              Total Contract Value ₹
            </label>
            <input
              type="number" min="0"
              value={form.total_value}
              onChange={e => setForm({ ...form, total_value: e.target.value })}
              placeholder="e.g. 500000"
              className="border p-2 rounded-xl w-full text-sm outline-none focus:ring-2 focus:ring-green-300"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">
              Start Date
            </label>
            <input type="date"
              value={form.start_date}
              onChange={e => setForm({ ...form, start_date: e.target.value })}
              className="border p-2 rounded-xl w-full text-sm outline-none" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">
              End Date
            </label>
            <input type="date"
              value={form.end_date}
              onChange={e => setForm({ ...form, end_date: e.target.value })}
              className="border p-2 rounded-xl w-full text-sm outline-none" />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1">
            Description
          </label>
          <textarea rows={2}
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder="Optional project description..."
            className="border p-2 rounded-xl w-full text-sm outline-none resize-none" />
        </div>
      </div>

      {/* CONTACTS */}
      <div className="bg-white rounded-xl shadow-sm p-5 space-y-3">
        <div className="flex justify-between items-center">
          <h2 className="font-semibold text-gray-700">Site Contacts</h2>
          <button onClick={addContact}
            className="text-sm text-green-600 font-semibold hover:underline">
            + Add Contact
          </button>
        </div>
        <p className="text-xs text-gray-400">
          e.g. Project Manager, AEE, Composite Engineer
        </p>
        {contacts.map((c, i) => (
          <div key={i} className="grid grid-cols-3 gap-2 items-center">
            <input
              placeholder="Name"
              value={c.name}
              onChange={e => updateContact(i, "name", e.target.value)}
              className="border p-2 rounded-lg text-sm outline-none" />
            <input
              placeholder="Role (e.g. PM)"
              value={c.role}
              onChange={e => updateContact(i, "role", e.target.value)}
              className="border p-2 rounded-lg text-sm outline-none" />
            <div className="flex gap-2">
              <input
                placeholder="Phone"
                value={c.phone}
                onChange={e => updateContact(i, "phone", e.target.value)}
                className="border p-2 rounded-lg text-sm outline-none flex-1" />
              {contacts.length > 1 && (
                <button onClick={() => removeContact(i)}
                  className="text-red-400 hover:text-red-600 text-lg">✕</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* CHECKLIST TEMPLATE */}
      <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-700">Checklist Template</h2>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="load_template"
              checked={form.load_template}
              onChange={e => setForm({ ...form, load_template: e.target.checked })}
              className="w-4 h-4 accent-green-600"
            />
            <label htmlFor="load_template"
              className="text-sm text-gray-600 cursor-pointer">
              Load checklist
            </label>
          </div>
        </div>

        {form.load_template && (
          <div className="space-y-3">
            <p className="text-xs text-gray-400">
              Select one or more trades — mixing e.g. VRF + Chiller in one project loads both checklists together, tagged separately:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(TEMPLATE_MAP).filter(([k]) => k !== "none").map(([key, t]) => {
                const isSelected = templateKeys.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setTemplateKeys(prev => {
                        const next = isSelected
                          ? prev.filter(k => k !== key)
                          : [...prev, key];
                        setForm(f => ({ ...f, template_keys: next }));
                        return next;
                      });
                    }}
                    className={`p-3 rounded-xl border-2 text-left text-xs font-semibold transition ${
                      isSelected
                        ? "border-green-500 bg-green-50 text-green-700"
                        : "border-gray-200 text-gray-600 hover:border-green-300"
                    }`}>
                    <div className="flex items-center gap-1.5">
                      {isSelected && <span className="text-green-600">✓</span>}
                      <span className="font-semibold">{t.label}</span>
                    </div>
                    <div className="text-gray-400 font-normal mt-0.5">
                      {key === "vrf"         && "37 items · 5 milestones"}
                      {key === "chiller"     && "37 items · 4 milestones"}
                      {key === "ducted"      && "28 items · 3 milestones"}
                      {key === "split"       && "15 items · 2 milestones"}
                      {key === "precision"   && "20 items · 2 milestones"}
                      {key === "ventilation" && "19 items · 3 milestones"}
                      {key === "supply"      && "8 items · 2 milestones"}
                      {key === "amc"         && "12 items · 0 milestones"}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Selected template(s) info */}
            <div className={`rounded-xl p-3 border ${
              templateKeys.length === 0
                ? "bg-red-50 border-red-200"
                : "bg-green-50 border-green-200"
            }`}>
              {templateKeys.length === 0 ? (
                <p className="text-xs text-red-600 font-semibold">
                  ⚠️ Select at least one trade, or turn off "Load checklist" above.
                </p>
              ) : (
                <>
                  <p className="text-xs text-green-700 font-semibold">
                    ✅ Selected ({templateKeys.length}): {templateKeys.map(k => TEMPLATE_MAP[k]?.label).join(", ")}
                  </p>
                  <p className="text-xs text-green-600 mt-0.5">
                    {templateKeys.length > 1
                      ? "Each trade's checklist loads as its own tagged category set when the project is created."
                      : "Checklist will auto-load when project is created."}
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {!form.load_template && (
          <p className="text-xs text-gray-400">
            No checklist will be loaded. You can add items manually later.
          </p>
        )}
      </div>

      {/* SAVE */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-sm disabled:opacity-50 transition">
        {saving ? "Creating..." : "Create Project ✅"}
      </button>

    {/* QUICK CREATE — Client */}
      <QuickCreateModal
        open={qcOpen === "client"}
        onClose={() => setQcOpen(null)}
        title="Quick Create — Client"
      >
        <QuickCreateClient
          companyId={company_id}
          onCreated={(newClient) => {
            setClients(prev => [...prev, newClient]);
            setForm(f => ({
              ...f,
              client_id:   newClient.id,
              client_name: newClient.client_name,
              site_id: "", site_name: "",
            }));
            fetchSites(newClient.id);
            setQcOpen(null);
          }}
          onCancel={() => setQcOpen(null)}
        />
      </QuickCreateModal>

      {/* QUICK CREATE — Site */}
      <QuickCreateModal
        open={qcOpen === "site"}
        onClose={() => setQcOpen(null)}
        title="Quick Create — Site"
      >
        <QuickCreateSite
          companyId={company_id}
          clientId={form.client_id}
          clientName={form.client_name}
          onCreated={(newSite) => {
            setSites(prev => [...prev, newSite]);
            setForm(f => ({
              ...f,
              site_id:   newSite.id,
              site_name: newSite.site_name,
            }));
            setQcOpen(null);
          }}
          onCancel={() => setQcOpen(null)}
        />
      </QuickCreateModal>

    </div>
  );
}